import { FC, useMemo } from 'react'
import {
    Grid,
    IconButton,
    makeStyles,
    Paper,
    Typography,
    SvgIcon,
    Button,
    Divider,
    useMediaQuery,
    useTheme,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/EditRounded'
import WarningIcon from '@material-ui/icons/WarningRounded'
import { ReactComponent as DocumentIcon } from '../../../../../images/document.svg'
import TooltipControl from '../../../../../control/tooltip/tooltip'
import useTranslate from '../../../../../i18n/useTranslate'
import { convertDateToFormatEN, convertDateToFormatTH } from '../../../../../utilities/date'
import projectStatus from '../../../../../constants/projectStatus'
import useAuthenStore from '../../../../../stores/authen/authenStore'
import constUserGroup from '../../../../../constants/userGroup'
import useCheckPermission from '../../../../../utilities/checkPermission'
import { encryptionAES } from '../../../../../utilities/encryption'
import { generatePath, useHistory } from 'react-router-dom'
import { RoutePath } from '../../../../../router/routesConfig'
import { TReturnList } from '../searchType'
import DialogCoaButton from '../DialogCoaButton'
import DialogPending from '../DialogPending'
import ProjectStatusBadge from './ProjectStatusBadge'
import SubmittedStatusText from './SubmittedStatusText'
import DialogDeleteButton from '../DialogDeleteButton'
import IndexDocumentIcon from '../../../../../images/index_file.svg'
interface CardItemProps {
    data: TReturnList
    pendingProps: any
    onClickEdit: (data: TReturnList) => void
}

const CardItem: FC<CardItemProps> = ({ data, pendingProps, onClickEdit }) => {
    const translate = useTranslate()
    const { profile } = useAuthenStore()
    const history = useHistory()
    const classes = useStyles()
    const theme = useTheme()
    const isMobileView = useMediaQuery(theme.breakpoints.down('sm'))
    const isOfficer = useCheckPermission().isUserAdmin()

    const isTH = translate.languageStore.currentLanguage === translate.languageKey.th
    const isDirector = profile.userGroupId === constUserGroup.committee
    const isResearcher = profile.userGroupId === constUserGroup.researcher
    const isProcessing = data.projectStatusID === projectStatus.processing
    const considerText = isTH ? data.consider_type_name_th : data.consider_type_name_en

    const projectNo = data.projectNumber ?? '-'
    const fmt = (d?: string | null) => (d ? (isTH ? convertDateToFormatTH(d) : convertDateToFormatEN(d)) : '-')

    const researcher = useMemo(() => {
        const name = isTH ? (data.researcherName_th ?? '-') : (data.researcherName_en ?? '-')
        const role = isTH ? data.researcherTypeName_th : data.researcherTypeName_en
        return role ? `${name} (${role})` : name
    }, [data, isTH])

    const openPendingHistory = () => {
        if (!data.pendingStatusID || !isOfficer) return
        const payload = {
            submittedID: data.submittedID,
            th: {
                projectName: data.projectName_th,
                researcherName: data.researcherName_th,
                submitTypeName: data.submitTypeName_th,
            },
            en: {
                projectName: data.projectName_en,
                researcherName: data.researcherName_en,
                submitTypeName: data.submitTypeName_en,
            },
        }
        const path = generatePath(RoutePath.projectPendingHistory, {
            projectData: encryptionAES(JSON.stringify(payload)),
        })
        history.push({ pathname: path })
    }

    const renderListElementMobile = (row: TReturnList) => {
        const displayName = isTH ? (row.projectName_th ?? '-') : (row.projectName_en ?? '-')
        return (
            <Paper
                className={`${classes.item} ${row.highlight ? classes.overdue : ''}`}
                variant="outlined"
                onClick={() => onClickEdit(row)}
            >
                <Grid container alignItems="center">
                    <Grid container item xs={2} alignItems="center" justify="flex-start">
                        <IconButton aria-label="InfoIcon">
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Grid>

                    <Grid container item xs={8}>
                        <Grid item xs={12}>
                            <Typography variant="body2" style={{ fontWeight: 700 }}>
                                {projectNo}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography noWrap variant="body2" title={displayName}>
                                {displayName}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Grid container item xs={2} alignItems="center" justify="flex-end">
                        <ProjectStatusBadge data={row} isResearcher={isResearcher} isDot />
                    </Grid>
                </Grid>
            </Paper>
        )
    }

    if (isMobileView) return renderListElementMobile(data)

    const projectDetail = (
        <Grid container alignItems="flex-start" spacing={2} style={{ marginBottom: 0 }}>
            <Grid item xs={12} md="auto">
                <div className={classes.colBlock}>
                    <Typography variant="caption" color="textSecondary">
                        {translate.get('LABEL.PROJECT_NUMBER')}
                    </Typography>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {data.highlight && (
                            <WarningIcon fontSize="small" color="secondary" style={{ marginBottom: 2 }} />
                        )}
                        <Typography variant="body2">{projectNo}</Typography>
                    </div>
                </div>
            </Grid>

            <Grid item xs={12} md="auto">
                <div className={classes.colBlock}>
                    <Typography variant="caption" color="textSecondary">
                        {translate.get('LABEL.COA_NO')}
                    </Typography>
                    <DialogCoaButton projectInfo={data} />
                </div>
            </Grid>

            <Grid item xs>
                <div className={classes.colBlock}>
                    <Typography variant="caption" color="textSecondary">
                        {translate.get('LABEL.RESEARCHER_NAME')}
                    </Typography>
                    <Typography variant="body2" noWrap>
                        <div style={{ display: 'flex' }}>
                            <TooltipControl title={isTH ? data.projectName_th : data.projectName_en}>
                                <SvgIcon fontSize="small" component={DocumentIcon} style={{ marginRight: 2 }} />
                            </TooltipControl>
                            <TooltipControl title={researcher}>{researcher}</TooltipControl>
                        </div>
                    </Typography>
                </div>
            </Grid>

            <Grid item xs={6} md="auto">
                <div className={classes.colBlock}>
                    <Typography variant="caption" color="textSecondary">
                        {translate.get('LABEL.SUBMITTED_DATE')}
                    </Typography>
                    <Typography variant="body2" noWrap>
                        {fmt(data.submittedDate)}
                    </Typography>
                </div>
            </Grid>

            {!isResearcher && (
                <Grid item xs={6} md="auto">
                    <div className={classes.colBlock}>
                        {isDirector ? (
                            <TooltipControl title={translate.get('LABEL.COMMITTEE_MEETING_DATE')}>
                                <Typography variant="caption" color="textSecondary" noWrap>
                                    {translate.get('LABEL.COMMITTEE_MEETING_DATE')}
                                </Typography>
                            </TooltipControl>
                        ) : (
                            <Typography variant="caption" color="textSecondary" noWrap>
                                {translate.get('LABEL.EXPECTED_ANNOUNCEMENT_DATE')}
                            </Typography>
                        )}
                        <Typography variant="body2" noWrap>
                            {isDirector
                                ? data.committeeDueDate
                                    ? fmt(data.committeeDueDate)
                                    : '-'
                                : data.announceDate
                                  ? fmt(data.announceDate)
                                  : '-'}
                        </Typography>
                    </div>
                </Grid>
            )}

            {!isResearcher && data.stepDueDate && (
                <Grid item xs={12} md="auto">
                    <div className={classes.colBlock}>
                        {data.stepDueDays ? (
                            <TooltipControl
                                title={translate.get('LABEL.STEP_DUE_TOOLTIP', [String(data.stepDueDays)])}
                            >
                                <Typography variant="caption" color="textSecondary" noWrap>
                                    {translate.get('LABEL.STEP_DUE_DATE')}
                                </Typography>
                            </TooltipControl>
                        ) : (
                            <Typography variant="caption" color="textSecondary" noWrap>
                                {translate.get('LABEL.STEP_DUE_DATE')}
                            </Typography>
                        )}
                        <Typography
                            variant="body2"
                            noWrap
                            className={data.highlight ? classes.stepDueOverdue : undefined}
                        >
                            {fmt(data.stepDueDate)}
                            {data.highlight ? ` (${translate.get('LABEL.STEP_OVERDUE')})` : ''}
                        </Typography>
                    </div>
                </Grid>
            )}
        </Grid>
    )

    const projectStatusSection = (
        <Grid container justify="space-between" alignItems="flex-end">
            <Grid item>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item>
                        <div className={classes.colBlock}>
                            <Typography variant="caption" color="textSecondary">
                                {translate.get('LABEL.CERTIFICATE_SUBMISSION_TYPE')}
                            </Typography>
                            <Typography noWrap variant="body2" className={classes.submittedTypeCard}>
                                {isTH ? data.submitTypeName_th || '-' : data.submitTypeName_en || '-'}
                            </Typography>
                        </div>
                    </Grid>
                    
                    {!isResearcher && (
                    <Grid item>
                        <TooltipControl title={isTH ? data.projectFolder_th : data.projectFolder_en} placement="right">
                            <div style={{ display: 'flex' }}>
                                <img src={IndexDocumentIcon} alt="file icon" style={{ height: 40 }} />
                            </div>
                        </TooltipControl>
                    </Grid>
                    )}

                    {data.irb === 2 && (
                        <Grid item>
                            <Grid container direction="column">
                                <Grid item>
                                    <Typography noWrap variant="caption" color="textSecondary">
                                        {translate.get('LABEL.CONSIDER_TYPE')}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography
                                        noWrap
                                        variant="body2"
                                        className={considerText ? classes.submittedTypeCard : undefined}
                                    >
                                        {considerText ?? '-'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>

            <Grid item>
                <Grid container alignItems="flex-end" spacing={1}>
                    {isProcessing && !isResearcher && (
                        <Grid item>
                            <SubmittedStatusText data={data} />
                        </Grid>
                    )}

                    <Grid item>
                        <Grid container direction="column" alignItems="flex-end">
                            <Grid item>
                                <Typography variant="caption" color="textSecondary" style={{ marginBottom: 2 }}>
                                    {translate.get('LABEL.STATUS')}
                                </Typography>
                            </Grid>
                            <Grid item>
                                <ProjectStatusBadge data={data} isResearcher={isResearcher} />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )

    const projectAction = (
        <Grid container justify="space-between">
            <Grid item>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                    <DialogPending item={data} pendingProps={pendingProps} />
                    {data.pendingStatusID && isOfficer && (
                        <Button variant="contained" onClick={openPendingHistory}>
                            {translate.get('LABEL.PENDING_HISTORY')}
                        </Button>
                    )}
                </div>
            </Grid>

            <Grid item>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                    {isOfficer && data.canBeDeleted && (
                        <DialogDeleteButton item={data} pendingProps={pendingProps} />
                    )}
                    <Button variant="contained" startIcon={<EditIcon />} onClick={() => onClickEdit(data)}>
                        {translate.get('BUTTON.INTO_PROJECT')}
                    </Button>
                </div>
            </Grid>
        </Grid>
    )

    return (
        <Paper className={`${classes.item} ${data.highlight ? classes.overdue : ''}`} variant="outlined">
            {projectDetail}
            {projectStatusSection}
            <Divider className={classes.divider} />
            {projectAction}
        </Paper>
    )
}

const useStyles = makeStyles((theme) => ({
    item: {
        padding: theme.spacing(2.5),
        marginBottom: 10,
        position: 'relative',
        '&:hover': { border: `1px solid ${theme.palette.primary.main}` },
        borderRadius: 8,
    },
    divider: { marginBlock: '15px' },
    colBlock: { minWidth: 160 },
    submittedTypeCard: {
        height: '28px',
        width: 'fit-content',
        borderRadius: 8,
        background: 'linear-gradient(0deg, #EEEEEE, #EEEEEE)',
        border: '1px solid #DCDCDC',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 8px',
    },
    ellipsis: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
    colBlockFixed160: {
        minWidth: 160,
        flexShrink: 0,
    },
    colBlockFluid: {
        minWidth: 0,
    },
    overdue: {
        background: '#fff2f2',
    },
    stepDueOverdue: {
        color: '#D62E4B',
        fontWeight: 700,
    },
}))

export default CardItem
